import { type Dispatch, useMemo } from 'react';
import { Check, ChevronDown } from 'lucide-react';
import type { DateTime } from 'luxon';
import { motion, useReducedMotion } from 'motion/react';
import { AudiencePicker } from '@/components/campaigns/audience-picker';
import { LoaderButton } from '@/components/loader-button';
import { TemplateString } from '@/components/template-string';
import { Button } from '@/components/ui/button';
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '@/components/ui/collapsible';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { useBreakpoint } from '@/hooks/use-breakpoint';
import { useI18nContext } from '@/i18n/i18n-react';
import {
  CampaignFooter,
  CampaignHeader,
  CampaignLayout,
  CampaignMainContent,
  CampaignRightContent,
} from '@/layouts/campaign.layout';
import {
  type BlockingIssue,
  type ComposerAction,
  type ComposerField,
  type ComposerState,
  TITLE_SOFT_LIMIT,
  canSendTest,
} from '@/lib/campaign-composer-form';
import {
  CAMPAIGN_SCHEDULER_INTERVAL_MINUTES,
  type PrimaryAction,
} from '@/lib/campaign-schedule';
import { mobileLayer } from '@/lib/mobile-layer';
import { cn } from '@/lib/utils';
import { ComposerSection, FieldHint } from './composer-section';
import { ComposerSuccess } from './composer-success';
import { CONFIRM_BUTTON_CHECK_MS, useDelayedFlag } from './korza-studio';
import { KorzaStudioPreview } from './korza-studio-preview';
import {
  PreviewPlatformToggle,
  PushPreview,
  type PushPreviewPlatform,
} from './push-preview';
import { ScheduleField } from './schedule-field';
import { StoreLinkField } from './store-link-field';

export interface CampaignComposerPresenterProps {
  state: ComposerState;
  dispatch: Dispatch<ComposerAction>;
  blockingIssues: BlockingIssue[];
  primaryAction: PrimaryAction;
  now: DateTime;
  tenant: {
    slug: string;
    iconUrl: string | null | undefined;
    name: string;
    storeWebsiteDomain: string;
  };
  audiencesEnabled: boolean;
  isSubmitting: boolean;
  onSubmit(): void;
  onCancel(): void;
  onTest(): void;
  isTesting: boolean;
  previewPlatform: PushPreviewPlatform;
  onPreviewPlatformChange(platform: PushPreviewPlatform): void;
  /** The campaign was created: confirm it (button, form, Studio in step). */
  created: ComposerCreated | null;
  onCreateAnother(): void;
  /** An unsent draft of this store to offer back ("Salvo há 5 minutos"). */
  draft: { savedLabel: string } | null;
  onResumeDraft(): void;
  onDiscardDraft(): void;
}

export interface ComposerCreated {
  kind: 'scheduled' | 'sentNow';
  /** "Sai hoje, 19:00" ('' for "Agora"). */
  whenLabel: string;
  campaignId: string;
  /** Launch time, in the campaign's timezone. */
  at: DateTime;
}

const TEST_HINT_ID = 'composer-test-hint';

// Same breakpoint as CampaignRightContent, which hosts the Studio column.
const STUDIO_MIN_WIDTH = 1200;

/**
 * Campaign composer UI (PUSH-05 AC 1–3, 5; PUSH-06 AC 8; PUSH-08 AC 6).
 * Full-screen task: Cancelar on the left of the header, the lock screen
 * preview first in the DOM (sticky on top below 1024px, sticky right column
 * from 1024px up), the fields, and a footer that says what is missing and
 * exactly what the main button will do. Nothing depends on hover. From
 * 1200px the preview is the full-height Korza Studio column instead (iPhone
 * only, so no platform toggle). The fields come as numbered steps (a check
 * once each is complete) with plain-language help behind each (i); an
 * unsent draft of the store is offered back on top. Once created, the main button turns into a
 * check, then the form gives way to ComposerSuccess while the Studio travels
 * to the launch time (CONFIRM_* in korza-studio.ts).
 */
export function CampaignComposerPresenter({
  state,
  dispatch,
  blockingIssues,
  primaryAction,
  now,
  tenant,
  audiencesEnabled,
  isSubmitting,
  onSubmit,
  onCancel,
  onTest,
  isTesting,
  previewPlatform,
  onPreviewPlatformChange,
  created,
  onCreateAnother,
  draft,
  onResumeDraft,
  onDiscardDraft,
}: CampaignComposerPresenterProps) {
  const { LL, locale } = useI18nContext();
  const showStudio = useBreakpoint(STUDIO_MIN_WIDTH);
  const reduceMotion = useReducedMotion() ?? false;
  const showSuccess = useDelayedFlag(
    created !== null,
    reduceMotion ? 0 : CONFIRM_BUTTON_CHECK_MS
  );
  const buttonCheck = created !== null && !showSuccess;
  // Stable while `created` is, so the memoized Studio skips form re-renders.
  const studioConfirmation = useMemo(
    () =>
      created && {
        at: created.at,
        label:
          created.kind === 'sentNow'
            ? `${LL.campaignComposer.success.queuedTitle()} · ${LL.campaignComposer.success.queuedWhen({ minutes: CAMPAIGN_SCHEDULER_INTERVAL_MINUTES })}`
            : `${LL.campaignComposer.success.scheduledTitle()} · ${created.whenLabel}`,
      },
    [created, LL]
  );

  const hasIssue = (field: ComposerField) =>
    blockingIssues.some((issue) => issue.field === field);

  const issueLabel = (field: ComposerField): string => {
    switch (field) {
      case 'title':
        return LL.campaignComposer.issueFields.title();
      case 'body':
        return LL.campaignComposer.issueFields.body();
      case 'link':
        return LL.campaignComposer.issueFields.link();
      case 'schedule':
        return LL.campaignComposer.issueFields.schedule();
      case 'name':
        return LL.campaignComposer.issueFields.name();
      case 'utmCampaign':
        return LL.campaignComposer.issueFields.utmCampaign();
    }
  };
  // While name and UTM are still derived (title -> name -> UTM), a missing
  // name/UTM is only a consequence of the missing title/name: list the cause.
  const isMissing = (field: ComposerField) =>
    blockingIssues.some((i) => i.field === field && i.reason === 'missing');
  const statusIssues = blockingIssues.filter(
    (issue) =>
      !(
        issue.reason === 'missing' &&
        ((issue.field === 'name' && !state.nameEdited && isMissing('title')) ||
          (issue.field === 'utmCampaign' &&
            !state.utmEdited &&
            isMissing('name')))
      )
  );
  const fieldsOf = (reason: BlockingIssue['reason']) =>
    statusIssues
      .filter((issue) => issue.reason === reason)
      .map((issue) => issueLabel(issue.field))
      .join(', ');
  const missing = fieldsOf('missing');
  const invalid = fieldsOf('invalid');
  const statusText = [
    missing && LL.campaignComposer.footer.missing({ fields: missing }),
    invalid && LL.campaignComposer.footer.fix({ fields: invalid }),
  ]
    .filter(Boolean)
    .join(' · ');

  const primaryLabel = (() => {
    switch (primaryAction.kind) {
      case 'sendNow':
        return LL.campaignComposer.footer.sendNow();
      case 'scheduleIncomplete':
        return LL.campaignComposer.footer.schedule();
      case 'schedule':
        if (primaryAction.day === 'today') {
          return LL.campaignComposer.footer.scheduleToday({
            time: primaryAction.time,
          });
        }
        if (primaryAction.day === 'tomorrow') {
          return LL.campaignComposer.footer.scheduleTomorrow({
            time: primaryAction.time,
          });
        }
        return LL.campaignComposer.footer.scheduleDate({
          date: primaryAction.dateLabel,
          time: primaryAction.time,
        });
    }
  })();

  const testable = canSendTest(state);
  const canSubmit =
    blockingIssues.length === 0 && primaryAction.kind !== 'scheduleIncomplete';

  return (
    <CampaignLayout>
      {showSuccess ? (
        <CampaignHeader
          title={LL.campaignComposer.header.title()}
          backTo={`/${tenant.slug}/campaigns`}
          className="max-md:px-4"
        />
      ) : (
        <CampaignHeader
          title={LL.campaignComposer.header.title()}
          leading={
            <Button
              type="button"
              variant="ghost"
              className={cn('-ml-3 shrink-0 px-3', mobileLayer.touch)}
              onClick={onCancel}
            >
              {LL.campaignComposer.header.cancel()}
            </Button>
          }
          className="max-md:px-4"
        />
      )}
      <CampaignMainContent
        className={cn(
          'max-md:p-4 max-md:pt-0 lg:pt-6',
          showSuccess && 'flex items-center justify-center'
        )}
      >
        {created && showSuccess ? (
          <ComposerSuccess
            kind={created.kind}
            whenLabel={created.whenLabel}
            campaignId={created.campaignId}
            tenantSlug={tenant.slug}
            onCreateAnother={onCreateAnother}
          />
        ) : (
          <motion.div
            inert={buttonCheck}
            initial={false}
            animate={{ opacity: buttonCheck ? 0 : 1, y: buttonCheck ? -8 : 0 }}
            transition={{
              delay: buttonCheck ? (CONFIRM_BUTTON_CHECK_MS / 1000) * 0.5 : 0,
              duration: reduceMotion ? 0 : 0.3,
            }}
            className={cn(
              'grid gap-6',
              // Next to the Studio: on the header's left edge, reading width.
              showStudio
                ? 'max-w-[720px]'
                : 'mx-auto max-w-5xl lg:grid-cols-[minmax(0,1fr)_360px] lg:items-start'
            )}
          >
            {!showStudio && (
              <div
                data-testid="composer-preview"
                className="sticky top-0 z-10 -mx-4 space-y-2 bg-background px-4 pb-2 pt-4 md:-mx-6 md:px-6 lg:order-last lg:mx-0 lg:self-start lg:px-0 lg:pt-0"
              >
                <PushPreview
                  platform={previewPlatform}
                  title={state.title}
                  body={state.body}
                  iconUrl={tenant.iconUrl}
                  appName={tenant.name}
                  locale={locale}
                />
                <PreviewPlatformToggle
                  value={previewPlatform}
                  onChange={onPreviewPlatformChange}
                />
              </div>
            )}

            <div className="min-w-0 space-y-10">
              {draft && (
                <div
                  role="region"
                  aria-label={LL.campaignComposer.draft.title()}
                  className="flex flex-col gap-3 rounded-lg border border-primary/30 bg-primary/5 p-4 sm:flex-row sm:items-center"
                >
                  <div className="min-w-0 flex-1 text-sm">
                    <p className="font-medium">
                      {LL.campaignComposer.draft.title()}
                    </p>
                    <p className="text-muted-foreground">
                      {LL.campaignComposer.draft.description({
                        when: draft.savedLabel,
                      })}
                    </p>
                  </div>
                  <div className="flex shrink-0 gap-2">
                    <Button
                      type="button"
                      variant="ghost"
                      className={mobileLayer.touch}
                      onClick={onDiscardDraft}
                    >
                      {LL.campaignComposer.draft.discard()}
                    </Button>
                    <Button
                      type="button"
                      className={mobileLayer.touch}
                      onClick={onResumeDraft}
                    >
                      {LL.campaignComposer.draft.resume()}
                    </Button>
                  </div>
                </div>
              )}

              <ComposerSection
                step={1}
                title={LL.campaignComposer.sections.message()}
                complete={!hasIssue('title') && !hasIssue('body')}
                hint={LL.campaignComposer.sections.messageHint()}
              >
                <div className="space-y-2">
                  <div className="flex items-center gap-1">
                    <Label htmlFor="composer-title">
                      {LL.campaignComposer.fields.title()}
                    </Label>
                    <FieldHint
                      label={LL.campaignComposer.sections.hintLabel({
                        section: LL.campaignComposer.fields.title(),
                      })}
                    >
                      {LL.campaignComposer.hints.title()}
                    </FieldHint>
                    <span className="ml-auto text-xs tabular-nums text-muted-foreground">
                      {LL.campaignComposer.fields.characters({
                        count: state.title.length,
                      })}
                    </span>
                  </div>
                  <Input
                    id="composer-title"
                    value={state.title}
                    placeholder={LL.campaignComposer.fields.titlePlaceholder()}
                    onChange={(e) =>
                      dispatch({ type: 'setTitle', value: e.target.value })
                    }
                    className={mobileLayer.input}
                  />
                  {state.title.length > TITLE_SOFT_LIMIT && (
                    <p className="text-sm text-amber-700 dark:text-amber-400">
                      {LL.campaignComposer.fields.titleTooLong({
                        limit: TITLE_SOFT_LIMIT,
                      })}
                    </p>
                  )}
                </div>

                <div className="space-y-2">
                  <div className="flex items-center gap-1">
                    <Label htmlFor="composer-body">
                      {LL.campaignComposer.fields.body()}
                    </Label>
                    <FieldHint
                      label={LL.campaignComposer.sections.hintLabel({
                        section: LL.campaignComposer.fields.body(),
                      })}
                    >
                      {LL.campaignComposer.hints.body()}
                    </FieldHint>
                    <span className="ml-auto text-xs tabular-nums text-muted-foreground">
                      {LL.campaignComposer.fields.characters({
                        count: state.body.length,
                      })}
                    </span>
                  </div>
                  <Textarea
                    id="composer-body"
                    value={state.body}
                    rows={3}
                    placeholder={LL.campaignComposer.fields.bodyPlaceholder()}
                    onChange={(e) =>
                      dispatch({ type: 'setBody', value: e.target.value })
                    }
                    className={mobileLayer.input}
                  />
                </div>
              </ComposerSection>

              <ComposerSection
                step={2}
                title={LL.campaignComposer.sections.destination()}
                complete={!hasIssue('link')}
                hint={LL.campaignComposer.sections.destinationHint()}
              >
                <StoreLinkField
                  mode={state.linkMode}
                  path={state.linkPath}
                  link={state.link}
                  domain={tenant.storeWebsiteDomain}
                  onModeChange={(value) =>
                    dispatch({
                      type: 'setLinkMode',
                      value,
                      domain: tenant.storeWebsiteDomain,
                    })
                  }
                  onPathChange={(value, normalize) =>
                    dispatch({
                      type: 'setLinkPath',
                      value,
                      domain: tenant.storeWebsiteDomain,
                      normalize,
                    })
                  }
                />
              </ComposerSection>

              <ComposerSection
                step={3}
                title={LL.campaignComposer.sections.schedule()}
                complete={
                  !hasIssue('schedule') &&
                  primaryAction.kind !== 'scheduleIncomplete'
                }
                hint={LL.campaignComposer.sections.scheduleHint()}
              >
                <ScheduleField
                  value={state.schedule}
                  timezone={state.timezone}
                  now={now}
                  onChange={(value) => dispatch({ type: 'setSchedule', value })}
                  onTimezoneChange={(value) =>
                    dispatch({ type: 'setTimezone', value })
                  }
                />
              </ComposerSection>

              {audiencesEnabled && (
                <ComposerSection
                  step={4}
                  title={LL.campaignComposer.sections.audience()}
                  complete
                >
                  <AudiencePicker
                    tenantSlug={tenant.slug}
                    value={state.audienceId}
                    onChange={(value) =>
                      dispatch({ type: 'setAudience', value })
                    }
                    locale={locale}
                  />
                </ComposerSection>
              )}

              <Collapsible
                open={state.trackingOpen}
                onOpenChange={() => dispatch({ type: 'toggleTracking' })}
                className="rounded-lg border max-md:rounded-mobile-card"
              >
                <CollapsibleTrigger
                  className={cn(
                    'flex w-full items-center gap-3 px-4 py-3 text-left',
                    mobileLayer.touch
                  )}
                >
                  <span className="min-w-0 flex-1">
                    <span className="block text-sm font-medium">
                      {LL.campaignComposer.tracking.title()}
                    </span>
                    {!state.trackingOpen && (
                      <span className="block truncate text-sm text-muted-foreground">
                        {LL.campaignComposer.tracking.summary({
                          name: state.name || '—',
                          utm: state.utmCampaign || '—',
                        })}
                      </span>
                    )}
                  </span>
                  <ChevronDown
                    aria-hidden
                    className={cn(
                      'size-4 shrink-0 transition-transform',
                      state.trackingOpen && 'rotate-180'
                    )}
                  />
                </CollapsibleTrigger>
                <CollapsibleContent className="space-y-4 px-4 pb-4">
                  <div className="space-y-2">
                    <Label htmlFor="composer-name">
                      {LL.campaignComposer.tracking.name()}
                    </Label>
                    <Input
                      id="composer-name"
                      value={state.name}
                      onChange={(e) =>
                        dispatch({ type: 'setName', value: e.target.value })
                      }
                      className={mobileLayer.input}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="composer-utm">
                      {LL.campaignComposer.tracking.utm()}
                    </Label>
                    <Input
                      id="composer-utm"
                      value={state.utmCampaign}
                      aria-invalid={state.utmAlreadyUsed || undefined}
                      aria-describedby={
                        state.utmAlreadyUsed ? 'composer-utm-error' : undefined
                      }
                      onChange={(e) =>
                        dispatch({ type: 'setUtm', value: e.target.value })
                      }
                      className={cn(
                        mobileLayer.input,
                        state.utmAlreadyUsed && 'border-destructive'
                      )}
                    />
                    {state.utmAlreadyUsed && (
                      <p
                        id="composer-utm-error"
                        className="text-sm text-destructive"
                      >
                        <TemplateString
                          templateString={LL.createCampaign.toast.utmCampaignValueAlreadyUsed()}
                          componentMap={{
                            UtmFieldLabel: () => (
                              <em>
                                {LL.createCampaign.toast.utmCampaignValueAlreadyUsedFieldLabel()}
                              </em>
                            ),
                          }}
                        />
                      </p>
                    )}
                  </div>
                </CollapsibleContent>
              </Collapsible>
            </div>
          </motion.div>
        )}
      </CampaignMainContent>
      <CampaignRightContent>
        <KorzaStudioPreview
          title={state.title}
          body={state.body}
          iconUrl={tenant.iconUrl}
          locale={locale}
          confirmation={studioConfirmation}
        />
      </CampaignRightContent>
      {/* Kept (invisible) once created: dropping its row would grow the
          Studio column and jump the phone mid-animation. */}
      <CampaignFooter
        className={cn(
          'flex-col items-stretch gap-2 max-md:px-4 max-md:py-3 md:flex-row md:items-center md:justify-between',
          showSuccess && 'invisible'
        )}
      >
        <div className="min-w-0 space-y-0.5 text-sm">
          <p role="status" className="text-muted-foreground">
            {statusText}
          </p>
          {!testable && (
            <p id={TEST_HINT_ID} className="text-muted-foreground">
              {LL.campaignComposer.footer.testNeeds()}
            </p>
          )}
        </div>
        <div className="flex shrink-0 gap-2 md:gap-4">
          <LoaderButton
            type="button"
            variant="outline"
            isLoading={isTesting}
            disabled={!testable}
            aria-describedby={testable ? undefined : TEST_HINT_ID}
            onClick={onTest}
            className={cn('max-md:flex-1', mobileLayer.touch)}
          >
            {LL.campaignComposer.footer.test()}
          </LoaderButton>
          <LoaderButton
            type="button"
            isLoading={isSubmitting && !buttonCheck}
            disabled={!canSubmit && !buttonCheck}
            aria-disabled={buttonCheck || undefined}
            onClick={buttonCheck ? undefined : onSubmit}
            className={cn('relative max-md:flex-[2]', mobileLayer.touch)}
          >
            {/* Created: the label makes way for a check, same button size. */}
            <span className={cn(buttonCheck && 'invisible')}>
              {primaryLabel}
            </span>
            {buttonCheck && (
              <motion.span
                aria-hidden
                data-testid="composer-created-check"
                className="absolute inset-0 flex items-center justify-center"
                initial={reduceMotion ? false : { scale: 0.3, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                transition={{ type: 'spring', stiffness: 520, damping: 24 }}
              >
                <Check className="size-5" strokeWidth={3} />
              </motion.span>
            )}
          </LoaderButton>
        </div>
      </CampaignFooter>
    </CampaignLayout>
  );
}
